// MyBookings.jsx — dynamic from backend /api/booking/user/:id
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MyContext } from '../../../../Context/MyContext';
import API from '../../../../Api/api';
import './MyBookings.css';
import AppIcon from '../../../../Components/AppIcon';

// Status config
const STATUS = {
  pending:   { label: 'Pending',   color: '#f59e0b', bg: '#FEF3C7' },
  approved:  { label: 'Approved',  color: '#16a34a', bg: '#DCFCE7' },
  rejected:  { label: 'Rejected',  color: '#dc2626', bg: '#FEE2E2' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#F3F4F6' },
};

const fmt = (n) => {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n/10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n/100000).toFixed(1)}L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const MyBookings = ({ setActiveView }) => {
  const { user, properties, imgUrl } = useContext(MyContext);
  const navigate = useNavigate();

  const [bookings,  setBookings]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [cancelling,setCancelling]= useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    if (!user?.id) return;
    fetchBookings();
  }, [user?.id]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const r = await API.get(`/booking/user/${user.id}`);
      setBookings(r.data || []);
    } catch (e) {
      console.log('fetchBookings:', e.message);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (booking) => {
    if (!window.confirm('Cancel this booking request?')) return;
    setCancelling(booking.id);
    try {
      await API.put(`/booking/${booking.id}`, { status: 'cancelled' });
      setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status: 'cancelled' } : b));
      toast.success('Booking cancelled');
    } catch {
      toast.error('Failed to cancel');
    } finally {
      setCancelling(null);
    }
  };

  // Filter
  const filtered = activeFilter === 'all'
    ? bookings
    : bookings.filter(b => b.status === activeFilter);

  const counts = {
    all:       bookings.length,
    pending:   bookings.filter(b => b.status === 'pending').length,
    approved:  bookings.filter(b => b.status === 'approved').length,
    rejected:  bookings.filter(b => b.status === 'rejected').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
  };

  const getProperty = (pid) => properties.find(p => p.id === pid);

  return (
    <div className="mbk-page">
      <div className="mbk-topbar">
        <button className="mbk-back" onClick={() => setActiveView('content')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back
        </button>
        <div>
          <h2 className="mbk-title">My Bookings</h2>
          <p className="mbk-sub">Track your property visit requests</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mbk-tabs">
        {['all','pending','approved','rejected','cancelled'].map(tab => (
          counts[tab] > 0 || tab === 'all' ? (
            <button
              key={tab}
              className={`mbk-tab ${activeFilter === tab ? 'active' : ''}`}
              onClick={() => setActiveFilter(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {counts[tab] > 0 && <span className="mbk-tab-count">{counts[tab]}</span>}
            </button>
          ) : null
        ))}
      </div>

      {loading ? (
        <div className="mbk-loading">
          <div className="mbk-spinner"/>
          <p>Loading bookings…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mbk-empty">
          <div className="mbk-empty-icon">📋</div>
          <h3>No bookings yet</h3>
          <p>Your site visit requests will appear here.</p>
          <button className="mbk-browse-btn" onClick={() => navigate('/properties')}>Browse Properties</button>
        </div>
      ) : (
        <div className="mbk-list">
          {filtered.map(booking => {
            const prop = getProperty(booking.property_id);
            const st   = STATUS[booking.status] || STATUS.pending;
            const thumb = prop?.thumbnail ? (imgUrl ? imgUrl(prop.thumbnail) : prop.thumbnail) : null;
            const canCancel = ['pending', 'approved'].includes(booking.status);

            return (
              <div key={booking.id} className="mbk-card">
                {/* Property thumbnail */}
                <div className="mbk-img">
                  {thumb
                    ? <img src={thumb} alt="property"/>
                    : <div className="mbk-img-ph"><AppIcon name="home" size={24} /></div>
                  }
                </div>

                <div className="mbk-body">
                  <div className="mbk-card-top">
                    <div>
                      <h3 className="mbk-prop-name">
                        {prop?.location || prop?.address || `Property #${booking.property_id}`}
                      </h3>
                      {prop?.price && <span className="mbk-price">{fmt(prop.price)}</span>}
                    </div>
                    {/* Status badge */}
                    <span className="mbk-status" style={{ color: st.color, background: st.bg }}>
                      {st.label}
                    </span>
                  </div>

                  {/* Booking details */}
                  <div className="mbk-details">
                    <span><AppIcon name="calendar" size={14} /> {new Date(booking.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</span>
                    {booking.form_data?.date && <span><AppIcon name="calendar" size={14} /> Visit: {booking.form_data.date}</span>}
                    {booking.form_data?.time && <span><AppIcon name="clock" size={14} /> {booking.form_data.time}</span>}
                  </div>

                  {/* Status info */}
                  {booking.status === 'approved' && (
                    <div className="mbk-info-bar mbk-info-green">
                      <AppIcon name="verified" size={14} /> Visit confirmed! Our team will contact you soon.
                    </div>
                  )}
                  {booking.status === 'rejected' && (
                    <div className="mbk-info-bar mbk-info-red">
                      <AppIcon name="error" size={14} /> Visit request was not approved. Please try another date.
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mbk-actions">
                    {prop && (
                      <button className="mbk-btn mbk-view" onClick={() => navigate(`/property/${booking.property_id}`)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                        View Property
                      </button>
                    )}
                    {canCancel && (
                      <button
                        className="mbk-btn mbk-cancel"
                        onClick={() => handleCancel(booking)}
                        disabled={cancelling === booking.id}
                      >
                        {cancelling === booking.id ? 'Cancelling…' : 'Cancel Request'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyBookings;